import { BrandKitSource, Source } from 'types'
import { SortingDirection } from 'api/clients/FilesViewsClient'
import { Icon } from 'almanac-ui/icons/Types'
import { File, Globe, GoogleDrive } from 'blaze-ui/icons'
import { Pdf, Markdown, TextFile } from 'blaze-ui/icons/24'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'

export const SOURCE_MATERIAL_TYPES = [
  'Website',
  'AlmanacFile',
  'PlainText',
  'SourceMaterialsMediaObject',
  'ExternalIntegration',
] as const

export type SortColumn = 'name' | 'type' | 'added' | 'updated'

export const sortBrandKitSources = <T extends BrandKitSource<Source>>(
  sources: T[],
  sortBy: SortColumn,
  sortDirection: SortingDirection,
): T[] => {
  if (!sources) return []

  return [...sources].sort((a, b) => {
    let comparison = 0

    if (sortBy === 'name') {
      const nameA = getDisplayName(a)
      const nameB = getDisplayName(b)
      comparison = nameA.localeCompare(nameB)
    } else if (sortBy === 'type') {
      comparison = a.source.type.localeCompare(b.source.type)
    } else if (sortBy === 'added') {
      comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
    } else if (sortBy === 'updated') {
      comparison = new Date(a.updated_at || 0).getTime() - new Date(b.updated_at || 0).getTime()
    }

    return sortDirection === SortingDirection.Ascending ? comparison : -comparison
  })
}

export const getDisplayName = (source: BrandKitSource<Source>): string => {
  let displayName = source.name
  if (!displayName && isWebsiteSource(source) && 'title' in source.source) {
    displayName = source.source.title || displayName
  }
  if (!displayName) {
    displayName = source.source.type || 'Untitled'
  }
  return displayName
}

export const isWebsiteSource = (source: BrandKitSource<Source>): boolean => {
  return source.source.type === 'Website'
}

export const isExternalIntegrationSource = (source: BrandKitSource<Source>): boolean => {
  return source.source.type === 'ExternalIntegration'
}

export const isDocumentSource = (source: BrandKitSource<Source>): boolean => {
  return source.source.type === 'PlainText' || source.source.type === 'SourceMaterialsMediaObject'
}

export const getSourceFavicon = (source: BrandKitSource<Source>): string | undefined => {
  if (isWebsiteSource(source) && 'favicon' in source.source) {
    return source.source.favicon
  }
  if (isExternalIntegrationSource(source)) {
    const integration = source.source as ExternalIntegration
    return integration.picture || Silhouette
  }
  return undefined
}

export const getSourceAltName = (source: BrandKitSource<Source>): string | undefined => {
  if (isWebsiteSource(source) && 'url' in source.source) {
    return source.source.url
  }
  if (source.source.type === 'AlmanacFile' && 'name' in source.source) {
    return source.source.name
  }
  return undefined
}

export const getSourceType = (source: BrandKitSource<Source>): Source['type'] => {
  return source.source.type
}

export const getIntegrationProgress = (
  connectedIntegrations: Array<{ source: { provider: string } }>,
  displayedIntegrations: string[],
): { connectedCount: number; progressPercentage: number } => {
  const connectedProviders = new Set(connectedIntegrations.map((i) => i.source.provider))
  const connectedCount = displayedIntegrations.filter((p) => connectedProviders.has(p)).length
  const progressPercentage = (connectedCount / displayedIntegrations.length) * 100

  return { connectedCount, progressPercentage }
}

export const isIntegrationActive = (
  provider: string,
  connectedProviders: Set<string>,
  disconnectedIntegrations: Array<{ provider: string }>,
): boolean => {
  const isConnected = connectedProviders.has(provider)
  const isDisconnected = disconnectedIntegrations.some((i) => i.provider === provider)
  return isConnected && !isDisconnected
}

export const getSourceIcon = (source: BrandKitSource<Source>): { icon: Icon; color?: string } => {
  const sourceType = source.source.type

  if ('provider' in source.source && source.source.provider === 'google_drive') {
    return { icon: GoogleDrive }
  }

  if (sourceType === 'Website') {
    return { icon: Globe }
  }

  if (sourceType === 'AlmanacFile') {
    return { icon: File }
  }

  if (sourceType === 'PlainText') {
    return { icon: TextFile, color: 'var(--teal-50)' }
  }

  if (sourceType === 'SourceMaterialsMediaObject' && 'mime_type' in source.source) {
    const mimeType = source.source.mime_type
    if (mimeType === 'application/pdf') {
      return { icon: Pdf, color: 'var(--red-50)' }
    }
    if (mimeType === 'text/markdown') {
      return { icon: Markdown, color: 'var(--green-50)' }
    }
    if (mimeType === 'text/plain') {
      return { icon: TextFile, color: 'var(--teal-50)' }
    }
  }

  return { icon: File }
}
